import { getModuleList, envConfigs, defaultRemoteMode, subPackageName, envMock, version } from './config.js'
import { log, normalizeModule, filterRoutesByRule, getOwnerRoutes, showErrorTips, loadChunkFromEntryUrl } from './utils.js'
import { ENV } from '@/public/config'
import Vue from 'vue'
import { logReport } from '@/public/utils/common'

const {baseUrl: defaultBaseUrl, postfix: entryFileNamePostfix} = envConfigs[envMock || ENV || 'dev'] || {}

const subModuleReg = /\/(.+?)\/.*/ // /tms-xxx/abc  => tms-xxx

const installedModuleRoutes = {}

export let moduleConfigs = []

export function getMatchedModule(subModuleName) {
  if (!subModuleName) {
    return null
  }
  return moduleConfigs.find(config => config.activeRule.includes(subModuleName))
}

function getMatchedModuleByName(pkgName) {
  if (!pkgName) {
    return null
  }
  return moduleConfigs.find(config => config.name === pkgName)
}

function setPublicPath (module) {
  window.__erp_public_path__ = module.publicPath
  const publicPathKey = `__erp_public_path__${module.name}`
  window[publicPathKey] = module.publicPath
}

// 是否开启本地模式(只加载本地模块)
function isLocalMode () {
  if (defaultRemoteMode) {
    return false
  }
  // if (document.cookie.includes('gray=1')) {
  //   log('灰度环境暂不启用远程模块')
  //   return true
  // }
  const isRemoteSubPackageMode = sessionStorage.getItem('REMOTESUBPACKAGEMODE')
  if (isRemoteSubPackageMode) {
    log('手动启动小包环境远程模块')
    return false
  }
  if (subPackageName) {
    log('小包环境暂不启用远程模块')
    return true
  }
  if (!window.kyerplib) { // 远程模块依赖dll打包出来的kyerplib, 取不到kyerplib时
    log('kyerplib未安装暂不启用远程模块')
    return true
  }
  if (!ENV) { // 不属于dev/uat/stg等常规环境时
    return true
  }
  return false
}

// 已经配置过远程路由(rule)的，首次addRoutes之前要忽略掉，因为后期的addRoutes无法覆盖同path的路由
export function filterRoutes(routes) {
  if (isLocalMode()) {
    return routes
  }
  return filterRoutesByRule(routes, moduleConfigs)
}

function loadModuleChunk(moduleMeta, moduleLibraryName) {
  setPublicPath(moduleMeta)
  const url = moduleMeta.entry
  if (moduleLibraryName && window[moduleLibraryName] && window[moduleLibraryName].default) {
    return Promise.resolve()
  }
  return loadChunkFromEntryUrl(url)
}

export function getSubModuleName(to) {
  let result = to.path.match(subModuleReg)
  return result ? result[1] : null
}

export function loadModule(to, next, router) {
  if (isLocalMode()) {
    next()
    return
  }
  try {
    _loadModule(to, next, router)
  } catch (e) {
    next()
    console.error(e)
    showErrorTips(`远程包加载失败` + e)
  }
}

function _loadModule(to, next, router) {
  const subModuleName = getSubModuleName(to)
  const moduleMeta = getMatchedModule(subModuleName)

  // 匹配不到远程路由(模块), 继续原路由
  if (!moduleMeta) {
    next()
    return
  }

  to.matched && log(`${to.matched.length > 1 ? '匹配到' : '开始加载'}远程路由:`, subModuleName, to.path)

  const packageName = moduleMeta.name

  if (installedModuleRoutes[packageName]) {
    next()
  } else {
    installRemoteModule(moduleMeta, router).then(() => { // 加载入口js, 并注册路由
      log(`远程包安装成功: ${subModuleName} `)
      next({...to})
    }).catch(e => {
      next({path: '/remote-404', query: {reload: to.path, entry: moduleMeta.entry}})
      log(`远程包安装失败, 请检查远程服务[${subModuleName}], 入口: ${moduleMeta.entry}`, e)
      try {
        logReport({
          menuId: '2820215716730110',
          logId: 'install-remote-module-failed',
          log: JSON.stringify(moduleMeta.entry)
        })
      } catch (e) {
        console.error(e)
      }
    })
  }
}

// 通过模块配置信息加载远程模块, 可选择是否插入路由表
export function installRemoteModule (moduleMeta, router) {
  const libraryName = moduleMeta.name + 'RemoteLibrary'
  const packageName = moduleMeta.name
  return new Promise((resolve, reject) => {
    loadModuleChunk(moduleMeta, libraryName).then(_ => {
      const res = window[libraryName]
      if (!res) {
        throw new Error('[ERP Remote Module] remote library load fail: ', libraryName)
      }
      if (router) {
        try {
          installedModuleRoutes[packageName] = true // router.addRoutes()会自动再次发起路由跳转，标记位设置在addRoutes之前
          router.addRoutes(getOwnerRoutes(res.default, moduleMeta.activeRule))
        } catch (e) {
          installedModuleRoutes[packageName] = false
          throw e
        }
      }
      resolve(res)
    }).catch(e => {
      reject(e)
    })
  })
}

// 对模块配置补充，插入publicPath, entry
function initModuleConfigs() {
  let baseModuleConfigs = window.__ERP_REMOTE_MODULE_LIST__ = getModuleList(envMock || ENV)
  moduleConfigs = normalizeModule(baseModuleConfigs, defaultBaseUrl, entryFileNamePostfix)
}

// 预加载模块
/* eslint-disable no-unused-vars */
function handelPreload (router) {
  const preloadModules = moduleConfigs.filter(module => module.preload)
  function iterate() {
    if (preloadModules.length === 0) return
    installRemoteModule(preloadModules.shift(), router).then(iterate).catch(iterate)
  }
  iterate() // 串行加载，防止publicPath错乱
}

// 动态导入, 类似webpack5模块联邦
export function importRemote (libPath, router) {
  const [pkgName, libName] = libPath.split('/')
  const moduleMeta = getMatchedModuleByName(pkgName)
  if (!moduleMeta) {
    throw new Error(`[ERP Remote Module] module[${pkgName}] not exist`)
  }
  const asyncComponent = function(resolve, reject) {
    installRemoteModule(moduleMeta, router).then(function(res) {
      if (res.exportLibs && res.exportLibs[libName]) {
        if (typeof res.exportLibs[libName] !== 'function') {
            throw new Error(`[ERP Remote Module] dynamically imported modules [${libPath}] must be () => Promise`)
        }
        res.exportLibs[libName]().then(lib => {
          resolve && resolve(lib)
        })
      } else {
        throw new Error(`[ERP Remote Module] dynamically imported modules[${libPath}] not exist, Please confirm the path is correct`)
      }
    }).catch(function(e) {
      reject && reject(e)
      throw new Error(e)
    })
  }
  asyncComponent.errorComp = Vue.extend({render: h => h('div', '加载远程组件失败.')})
  return asyncComponent
}

export function initRemoteModule(router) {
  log('状态:', isLocalMode() ? '远程模式已关闭' : '远程模式已启用')
  initModuleConfigs()
  window.__ERP_REMOTE_IMPORT__ = importRemote
  window.__ERP_REMOTE_MODULE_INSTALLED__ = installedModuleRoutes
  if (isLocalMode()) {
    return
  }
  handelPreload(router)
  log('version: ', version)
}



// WEBPACK FOOTER //
// ./src/layout/router/remote/index.js